import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Sidebar from "./components/Sidebar";
import UniversalSearch from "./components/v2/UniversalSearch";
import ChatWidget from "./components/v2/ChatWidget";
import ChatSidebar from "./components/v2/ChatSidebar";
import OnboardingTour from "./components/v2/OnboardingTour";
import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import RFPAnalysis from "./pages/RFPAnalysis";
import RFPList from "./pages/RFPList";
import Compliance from "./pages/Compliance";
import Login from "./pages/Login";
import Settings from "./pages/Settings";
import V2SettingsShell from "./pages/v2/SettingsShell";
import ProposalGenerator from "./pages/ProposalGenerator";
import HistoricalBids from "./pages/HistoricalBids";
import Tools from "./pages/Tools";
import ProductCoverageReport from "./pages/ProductCoverageReport";
import BidPriorityDashboard from "./pages/BidPriorityDashboard";
import SystemMonitoring from "./pages/SystemMonitoring";
import AuditLogs from "./pages/AuditLogs";
import HelpCenter from "./pages/HelpCenter";
import FineTuning from "./pages/FineTuning";
import V2ProjectsList from "./pages/v2/ProjectsList";
import V2ProjectDetail from "./pages/v2/ProjectDetail";
import V2ProjectSectionConfig from "./pages/v2/ProjectSectionConfigPage";
import V2ProjectWorkflow from "./pages/v2/ProjectWorkflowPage";
import V2ApprovalsInbox from "./pages/v2/ApprovalsInbox";
import V2SearchPage from "./pages/v2/SearchPage";
import V2ChatPage from "./pages/v2/ChatPage";
import V2NotificationsPage from "./pages/v2/NotificationsPage";
import V2DepartmentsPage from "./pages/v2/DepartmentsPage";
import V2HistoricalBidsPage from "./pages/v2/HistoricalBidsPage";
import V2TemplatesPage from "./pages/v2/TemplatesPage";
import V2CatalogPage from "./pages/v2/CatalogPage";
import V2ProductDetailPage from "./pages/v2/ProductDetailPage";
import V2LibraryPage from "./pages/v2/LibraryPage";
import TenderWorkspace from "./pages/TenderWorkspace";
import DirectMessagePage from "./pages/v2/DirectMessagePage";
import V2ReportsPage from "./pages/v2/ReportsPage";
import V2QuotationsArchivePage from "./pages/v2/QuotationsArchivePage";
import V2FeedPage from "./pages/v2/FeedPage";
import V2CompetitorsPage from "./pages/v2/CompetitorsPage";
import V2CompetitorDetailPage from "./pages/v2/CompetitorDetailPage";
import V2ApprovalPoliciesPage from "./pages/v2/ApprovalPoliciesPage";
import V2StageGatesPage from "./pages/v2/StageGatesPage";
import V2ApproverRolesPage from "./pages/v2/ApproverRolesPage";
import V2WorkflowsPage from "./pages/v2/WorkflowsPage";
import V2UsersPage from "./pages/v2/UsersPage";
import V2DashboardPage from "./pages/v2/DashboardPage";
import V2AuditPage from "./pages/v2/AuditPage";
import { Loader2 } from "lucide-react";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main
        className="flex-1 ml-0 lg:ml-[260px] bg-[#F1F5F9] min-h-screen flex flex-col"
        style={{ marginRight: "var(--chat-sidebar-width, 0px)", transition: "margin-right 0.2s" }}
      >
        <header className="sticky top-0 z-30 h-12 bg-[#0F172A] flex items-center px-4 gap-4 border-b border-white/10">
          <UniversalSearch />
        </header>
        <div className="flex-1">{children}</div>
      </main>
      <ChatSidebar />
      <ChatWidget />
      <OnboardingTour />
    </div>
  );
};
// ChatWidget reads window.location internally to scope to current project

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      {/* Great Simplification: default landing is the Tenders list, not a
          metrics dashboard. The dashboard route still exists for direct
          links / Settings shortcut. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/tenders" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/dashboard"
        element={<ProtectedRoute><AppLayout><V2DashboardPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/audit"
        element={<ProtectedRoute><AppLayout><V2AuditPage /></AppLayout></ProtectedRoute>}
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <Upload />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/rfp-list"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RFPList />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Great Simplification: /tenders is the new home of RFP work. /tender/:rfpId
          is the single workspace that wraps Brief/Eligibility/BoM/Quotation/
          Proposal & Cover/Outcome as tabs. RFPList stays mounted at /tenders
          for now; v2 list page will replace it in the next pass. */}
      <Route
        path="/tenders"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RFPList />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tender/:rfpId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TenderWorkspace />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* v3 UX alias: /c/<rfpId> is the Slack-channel route that the new
          sidebar wires to. Same TenderWorkspace component, same data —
          we'll layer channel-specific chrome (Messages/Pins/Tasks tabs)
          on top in subsequent Stage 1 commits. */}
      <Route
        path="/c/:rfpId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TenderWorkspace />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Stage 2 PR 6 — Direct messages. /dm/new picks a teammate; /dm/:id
          opens the thread. AppLayout keeps the sidebar mounted. */}
      <Route
        path="/dm/:threadId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DirectMessagePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/rfp/:rfpId" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <RFPAnalysis />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/compliance" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <Compliance />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      {/* Great Simplification: /settings is now a tabbed shell that
          surfaces every governance / admin / scaffolding page (the v1
          Settings is inside it as the "Company" tab). Direct links to
          a specific tab work via ?tab=workflows etc. */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2SettingsShell />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/rfp/:rfpId/proposal" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProposalGenerator />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/historical-bids" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <HistoricalBids />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/tools" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <Tools />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product-coverage" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <ProductCoverageReport />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bid-priority" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <BidPriorityDashboard />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/system-monitoring" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <SystemMonitoring />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit-logs" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <AuditLogs />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <HelpCenter />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/fine-tuning" 
        element={
          <ProtectedRoute>
            <AppLayout>
              <FineTuning />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/projects"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2ProjectsList />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/projects/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2ProjectDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/projects/:id/section-config"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2ProjectSectionConfig />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/projects/:id/workflow"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2ProjectWorkflow />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/approvals"
        element={
          <ProtectedRoute>
            <AppLayout>
              <V2ApprovalsInbox />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/v2/search"
        element={<ProtectedRoute><AppLayout><V2SearchPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/chat"
        element={<ProtectedRoute><AppLayout><V2ChatPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/notifications"
        element={<ProtectedRoute><AppLayout><V2NotificationsPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/departments"
        element={<ProtectedRoute><AppLayout><V2DepartmentsPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/historical"
        element={<ProtectedRoute><AppLayout><V2HistoricalBidsPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/templates"
        element={<ProtectedRoute><AppLayout><V2TemplatesPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/catalog"
        element={<ProtectedRoute><AppLayout><V2CatalogPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/catalog/:productId"
        element={<ProtectedRoute><AppLayout><V2ProductDetailPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/reports"
        element={<ProtectedRoute><AppLayout><V2ReportsPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/quotations-archive"
        element={<ProtectedRoute><AppLayout><V2QuotationsArchivePage /></AppLayout></ProtectedRoute>}
      />
      {/* Great Simplification §6: Library = quotations + proposals + cover
          letters under one /v2/library tabbed page. Replaces the standalone
          /v2/quotations-archive once teams have migrated. */}
      <Route
        path="/v2/library"
        element={<ProtectedRoute><AppLayout><V2LibraryPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/feed"
        element={<ProtectedRoute><AppLayout><V2FeedPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/competitors"
        element={<ProtectedRoute><AppLayout><V2CompetitorsPage /></AppLayout></ProtectedRoute>}
      />
      {/* Phase 4 Competitor 360° — projects, products, certifications, prices */}
      <Route
        path="/v2/competitors/:competitorId"
        element={<ProtectedRoute><AppLayout><V2CompetitorDetailPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/approval-policies"
        element={<ProtectedRoute><AppLayout><V2ApprovalPoliciesPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/approver-roles"
        element={<ProtectedRoute><AppLayout><V2ApproverRolesPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/stage-gates"
        element={<ProtectedRoute><AppLayout><V2StageGatesPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/workflows"
        element={<ProtectedRoute><AppLayout><V2WorkflowsPage /></AppLayout></ProtectedRoute>}
      />
      <Route
        path="/v2/users"
        element={<ProtectedRoute><AppLayout><V2UsersPage /></AppLayout></ProtectedRoute>}
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
